import React from 'react';
import { CircularProgress, Dialog, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './SubscriptionDialog.module.css';

/**
 * Casca única dos popups do ciclo de assinatura (troca de plano, encaixe no plano,
 * cancelamento, recurso bloqueado): cabeçalho com título e subtítulo, corpo e
 * rodapé com a ação secundária à esquerda e as principais à direita.
 * Tela cheia no celular.
 *
 * Props:
 *   open, onClose
 *   title, subtitle  — texto ou nó
 *   icon             — nó opcional acima do título (estados de sucesso, recurso bloqueado)
 *   centered         — cabeçalho centralizado (estados de sucesso)
 *   maxWidth         — largura máxima em px (padrão 560)
 *   locked           — impede fechar (carregando ou sem saída, como o sucesso do upgrade)
 *   hideClose        — sem o botão de fechar no canto
 *   secondaryAction  — nó à esquerda do rodapé
 *   actions          — nó(s) à direita do rodapé
 */
const SubscriptionDialog = ({
  open,
  onClose,
  title,
  subtitle,
  icon = null,
  centered = false,
  maxWidth = 560,
  locked = false,
  hideClose = false,
  secondaryAction = null,
  actions = null,
  children,
}) => {
  const isPhone = useMediaQuery('(max-width:600px)');

  const handleClose = () => {
    if (!locked && onClose) onClose();
  };

  const hasFooter = Boolean(secondaryAction || actions);

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      fullScreen={isPhone}
      fullWidth
      maxWidth={false}
      PaperProps={{
        className: styles.paper,
        style: isPhone ? undefined : { maxWidth },
      }}
    >
      {!hideClose && (
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          disabled={locked}
          aria-label="Fechar"
        >
          <CloseIcon fontSize="inherit" />
        </button>
      )}

      <div className={`${styles.header} ${centered ? styles.headerCentered : ''}`}>
        {icon && <div className={styles.icon}>{icon}</div>}
        {title && <h2 className={styles.title}>{title}</h2>}
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>

      {children && <div className={styles.body}>{children}</div>}

      {hasFooter && (
        <div className={styles.footer}>
          <div className={styles.footerStart}>{secondaryAction}</div>
          <div className={styles.footerEnd}>{actions}</div>
        </div>
      )}
    </Dialog>
  );
};

const VARIANT_CLASS = {
  primary: styles.btnPrimary,
  text: styles.btnText,
  danger: styles.btnDanger,
  outlined: styles.btnOutlined,
};

/**
 * Botão do rodapé: primary (cor principal), text (secundária discreta),
 * outlined e danger (vermelho discreto). Com `loading`, mostra o giro e o
 * `loadingLabel` e fica desabilitado.
 */
export const DialogButton = ({
  variant = 'primary',
  loading = false,
  loadingLabel,
  disabled = false,
  children,
  ...rest
}) => (
  <button
    type="button"
    className={`${styles.btn} ${VARIANT_CLASS[variant] || styles.btnPrimary}`}
    disabled={disabled || loading}
    {...rest}
  >
    {loading && <CircularProgress size={16} sx={{ color: 'currentColor' }} />}
    {loading && loadingLabel ? loadingLabel : children}
  </button>
);

const NOTICE_CLASS = {
  error: styles.noticeError,
  warning: styles.noticeWarning,
  success: styles.noticeSuccess,
  info: styles.noticeInfo,
};

/** Quadro de aviso dentro do popup (erro de cartão, o que acontece com o resto...). */
export const DialogNotice = ({ tone = 'info', icon = null, children }) => (
  <div className={`${styles.notice} ${NOTICE_CLASS[tone] || styles.noticeInfo}`} role={tone === 'error' ? 'alert' : undefined}>
    {icon && <span className={styles.noticeIcon}>{icon}</span>}
    <div className={styles.noticeText}>{children}</div>
  </div>
);

export default SubscriptionDialog;
